import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Custom hook for admin authentication and authorization
 * Handles the race condition between currentUser and userProfile loading
 * 
 * @param {Array} allowedRoles - Array of allowed roles for this admin panel
 * @param {Array} allowedDepartments - Array of allowed departments (optional)
 * @param {string} redirectPath - Path to redirect to if access denied (default: '/dashboard')
 * @returns {Object} - { hasAccess, isLoading, error }
 */
export const useAdminAuth = (allowedRoles = [], allowedDepartments = [], redirectPath = '/dashboard') => {
  const { currentUser, userProfile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Wait for authentication to complete
    if (authLoading) {
      return;
    }

    // If no user, don't redirect immediately (wait for auth to load)
    if (!currentUser) {
      setIsLoading(false);
      return;
    }

    // Wait for userProfile to load (it might be null initially on refresh)
    if (currentUser && !userProfile) {
      // User is authenticated but profile not loaded yet, wait
      return;
    }

    // Both currentUser and userProfile are available
    const userRole = userProfile?.role?.toLowerCase();
    const userDept = userProfile?.department?.toLowerCase();
    
    // Check if user has access
    const hasRoleAccess = allowedRoles.some(role => 
      role.toLowerCase() === userRole
    );
    
    const hasDeptAccess = allowedDepartments.length === 0 || 
      allowedDepartments.some(dept => 
        dept.toLowerCase() === userDept
      );

    if (hasRoleAccess && hasDeptAccess) {
      setHasAccess(true);
      setError('');
    } else {
      setHasAccess(false);
      setError('Access denied. Insufficient privileges.');
      navigate(redirectPath);
    }

    setIsLoading(false);
  }, [currentUser, userProfile, authLoading, allowedRoles, allowedDepartments, redirectPath, navigate]);

  return { hasAccess, isLoading, error };
};

/**
 * Hook specifically for HR Admin access
 */
export const useHRAdminAuth = () => {
  return useAdminAuth(
    ['super_admin', 'superadmin', 'hr_admin', 'hradmin', 'admin'],
    ['hr', 'human resources', 'admin'],
    '/dashboard'
  );
};

/**
 * Hook specifically for Marketing Admin access
 */
export const useMarketingAdminAuth = () => {
  return useAdminAuth(
    ['super_admin', 'superadmin', 'marketing_admin', 'marketingadmin', 'admin'],
    [],
    '/dashboard'
  );
};

/**
 * Hook specifically for Super Admin access
 */
export const useSuperAdminAuth = () => {
  return useAdminAuth(
    ['super_admin', 'superadmin'],
    [],
    '/dashboard'
  );
};
